import { useContext, useState } from 'react';
import router from 'next/router';
import storageKeys from 'src/app_config/storage-keys.json';

import { Field, RichText, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import { ModalContext } from 'src/context/Modal/ModalContext';
import { RcnUserContext } from 'src/context/RcnUser/RcnUserContext';

import { FetchState } from 'src/enums/FetchState';

import { ComponentProps, SitecoreContextValue } from 'src/lib/ComponentProps';

import { mapLMSCourseToContentDetails } from 'src/mappers/models/lmsCourseMapper';

import { enrolRequest } from 'src/services/course/enrolRequest';

import { ContentDetailItem } from 'src/types/ContentDetailsItem';

import { Button } from 'src/components/basicContent/_Button/Button';
import { ButtonWithLoader } from 'src/components/basicContent/_ButtonWithLoader/ButtonWithLoader';

import styles from './CourseOverviewEnrolment.module.css';

type CourseOverviewEnrolmentProps = ComponentProps & {
	fields: {
		EnrolDisclaimerText?: Field<string>;
		CrmCode?: Field<string>;
	};
};

type LoadingStateProps = {
	[key: string]: CourseAction | FetchState;
	id: CourseAction;
	status: FetchState;
};

enum CourseAction {
	TryAgain,
	EnrolAndGoToCourse,
	EnrolAndCloseModal,
	NeedToBeLoggedInToEnrol,
}

export default function CourseOverviewEnrolment(props: CourseOverviewEnrolmentProps): JSX.Element {
	const { sitecoreContext } = useSitecoreContext<SitecoreContextValue>();
	const ModalState = useContext(ModalContext);
	const { authenticatedUserProfile } = useContext(RcnUserContext);

	const [loading, setLoading] = useState<LoadingStateProps>({
		id: CourseAction.EnrolAndCloseModal,
		status: FetchState.START,
	});

	const [agreedToTerms, setAgreedToTerms] = useState(false);

	if (!authenticatedUserProfile && typeof window !== 'undefined') {
		localStorage.removeItem('RecentlyEnrolledCourses');
	}

	const timer = (ms: number) => new Promise((res) => setTimeout(res, ms));

	const contentDetailsItem: ContentDetailItem | null = mapLMSCourseToContentDetails(
		props.rendering.dataSource ?? '',
		props.rendering.fields
	);

	const enrolUserOnCourse = async (courseAction: CourseAction) => {
		if (!props.fields?.CrmCode?.value || !authenticatedUserProfile?.contact?.contactId) {
			setLoading({
				...loading,
				id: CourseAction.NeedToBeLoggedInToEnrol,
				status: FetchState.ERROR,
			});
			return;
		}

		setLoading({
			...loading,
			id: courseAction,
			status: FetchState.LOADING,
		});
		setAgreedToTerms(true);

		const courseId = props.fields?.CrmCode?.value;
		const contactId = authenticatedUserProfile?.contact?.contactId;
		const response = await enrolRequest({
			ContactId: contactId,
			Code: courseId,
		});

		await timer(1000);

		if (response === FetchState.SUCCESS) {
			try {
				const recentlyEnrolledCourses = localStorage.getItem(storageKeys.LocalStorage.RecentlyEnrolledCourses);

				const entry = `${contactId},${courseId}|`.toLowerCase();
				if (!recentlyEnrolledCourses) {
					localStorage.setItem(storageKeys.LocalStorage.RecentlyEnrolledCourses, entry);
				} else if (!recentlyEnrolledCourses.toString().toLowerCase().includes(entry)) {
					localStorage.setItem(storageKeys.LocalStorage.RecentlyEnrolledCourses, recentlyEnrolledCourses + entry);
				}
			} catch (error) {
				console.log(error);
			}

			if (courseAction === CourseAction.EnrolAndGoToCourse) {
				router.push(contentDetailsItem?.Url || '');
			} else {
				setLoading({
					...loading,
					id: courseAction,
					status: FetchState.START,
				});
				ModalState.updateDisplayCourseEnrolModal(false);
			}
		} else {
			setLoading({
				...loading,
				id: courseAction,
				status: response,
			});

			if (courseAction === CourseAction.TryAgain) {
				await timer(1000);
				setLoading({
					...loading,
					id: courseAction,
					status: FetchState.START,
				});
			}
		}
	};

	const buttonWithLoader = ({ id, buttonText, onSubmit }: { id: CourseAction; buttonText: string; onSubmit: (id: string) => void }) => {
		return (
			<ButtonWithLoader
				id={id.toString()}
				className="white button"
				loginState={loading.id === id && loading.status !== FetchState.START ? loading.status : undefined}
				disabled={loading.id === id && loading.status !== FetchState.START ? loading.status >= 1 : !agreedToTerms}
				onSubmit={onSubmit}
			>
				{buttonText}
			</ButtonWithLoader>
		);
	};

	return (
		<div className={styles.openModalButtonContainer} data-display-pdf-download>
			<div className={styles.ContentOverviewCourseEnrolmentModal}>
				<div className={styles.enrolTermsAndConditionsText}>
					{loading.id === CourseAction.NeedToBeLoggedInToEnrol ? (
						<span className={styles.enrolButtonErrorMessage}>{sitecoreContext.dictionary.enrolRequiresLogin}</span>
					) : loading.status === FetchState.ERROR || loading.status === FetchState.WARNING || loading.id === CourseAction.TryAgain ? (
						<span className={styles.enrolButtonErrorMessage}>{sitecoreContext.dictionary.enrolErrorMessage}</span>
					) : loading.status === FetchState.SUCCESS ? (
						<span className={styles.enrolButtonSuccessMessage}>{sitecoreContext.dictionary.enrolSuccessMessage}</span>
					) : (
						<>
							<h4 className={styles.ModalHeaderText}>{sitecoreContext.dictionary.enrolModalHeader}</h4>
							<div className={styles.enrolTermsAndConditions}>
								<label htmlFor="enrolDisclaimer" className={styles.label}>
									<input
										type="checkbox"
										title="enrolDisclaimer"
										id="enrolDisclaimer"
										className={styles.enrolDisclaimerInput}
										onChange={() => setAgreedToTerms(!agreedToTerms)}
									/>
								</label>

								<RichText className={styles.disclaimerText} field={props.fields.EnrolDisclaimerText} />
							</div>
						</>
					)}
				</div>

				{loading.status === FetchState.ERROR || loading.status === FetchState.WARNING || loading.id === CourseAction.TryAgain ? (
					<div className={styles.enrolButtonContainer}>
						{buttonWithLoader({
							id: CourseAction.TryAgain,
							buttonText: `${sitecoreContext.dictionary.enrolButtonTryAgain}`,
							onSubmit: () => enrolUserOnCourse(CourseAction.TryAgain),
						})}
					</div>
				) : authenticatedUserProfile?.isActiveMember ? (
					buttonWithLoader({
						id: CourseAction.EnrolAndGoToCourse,
						buttonText: `${sitecoreContext.dictionary.enrolGoToCourse}`,
						onSubmit: () => enrolUserOnCourse(CourseAction.EnrolAndGoToCourse),
					})
				) : (
					<>
						<div className={styles.enrolButtonContainer}>
							{buttonWithLoader({
								id: CourseAction.EnrolAndCloseModal,
								buttonText: `${sitecoreContext.dictionary.enrolModalAgreeAndCloseButtonText}`,
								onSubmit: () => enrolUserOnCourse(CourseAction.EnrolAndCloseModal),
							})}
							{buttonWithLoader({
								id: CourseAction.EnrolAndGoToCourse,
								buttonText: `${sitecoreContext.dictionary.enrolModalAgreeAndGoToCourseButtonText}`,
								onSubmit: () => enrolUserOnCourse(CourseAction.EnrolAndGoToCourse),
							})}
						</div>

						<Button
							className={`buttonReset link linkHoverWeighted ${styles.cancelButton}`}
							onClick={() => ModalState.updateDisplayCourseEnrolModal(false)}
						>
							{sitecoreContext.dictionary.enrolModalCancelButtonText}
						</Button>
					</>
				)}
			</div>
		</div>
	);
}
